import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { KineticText } from "@/components/KineticText";
import Reveal from "@/components/Reveal";
import Stats from "@/components/Stats";
import EditorialMarquee from "@/components/EditorialMarquee";
import { BUSINESS, IMAGES, STATS, CUSTOMERS } from "@/lib/content";

const CHAPTERS = [
  {
    n: "01",
    title: "Heritage in the Market Yard",
    body: "Rooted in the bustling Karnavati Agriculture Market Yard at Vatva, our family name has stood for reliable, fresh vegetable supply for years. We know the rhythm of the mandi — the early mornings, the grading, the trust built handshake by handshake.",
    img: IMAGES.market,
  },
  {
    n: "02",
    title: "Transparency in Every Trade",
    body: "As a commission agent, our value is our honesty. Accurate weighing, clear invoicing, and pricing anchored to live mandi rates mean farmers and buyers alike get a fair deal — with no hidden charges, ever.",
    img: IMAGES.hands,
  },
  {
    n: "03",
    title: "A Network That Delivers",
    body: "Strong relationships with trusted growers and a broad base of wholesalers, retailers, hotels, and exporters let us guarantee fresh stock and timely supply — coordinating loading and transportation right to your destination.",
    img: IMAGES.hero,
  },
];

export default function About() {
  return (
    <>
      <SEO
        title="About Us"
        description="Learn about Pravin Hiralal Prajapati — a trusted wholesale vegetable supplier and commission agent rooted in Karnavati Market Yard, Ahmedabad, built on transparency and fresh daily supply."
        path="about"
      />

      <section className="relative bg-brand-forest text-brand-bone pt-40 pb-24 md:pt-52 md:pb-32 grain overflow-hidden">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 relative z-10">
          <span className="font-mono text-xs uppercase tracking-[0.28em] text-brand-leaf-light">About Us</span>
          <KineticText
            as="h1"
            lines={["Rooted in Karnavati", "Market Yard."]}
            className="font-display text-5xl md:text-8xl mt-6 leading-[0.92] tracking-tight"
          />
          <p className="mt-8 max-w-2xl text-brand-bone/70 text-lg leading-relaxed">
            {BUSINESS.name} is a trusted {BUSINESS.tagline} operating from {BUSINESS.location}. We supply fresh,
            high-quality vegetables directly from farmers to businesses across the region.
          </p>
        </div>
      </section>

      <EditorialMarquee />

      <section className="py-24 md:py-32 max-w-screen-2xl mx-auto px-6 md:px-12 space-y-24 md:space-y-40">
        {CHAPTERS.map((c, i) => (
          <div key={c.n} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
            <Reveal>
              <div className="overflow-hidden rounded-3xl aspect-[4/3] shadow-xl">
                <img src={c.img} alt={c.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <span className="font-mono text-sm text-brand-terra">Chapter {c.n}</span>
              <h2 className="font-display text-4xl md:text-5xl text-brand-forest tracking-tight mt-4 leading-none">{c.title}</h2>
              <p className="mt-6 text-brand-muted text-lg leading-relaxed">{c.body}</p>
            </Reveal>
          </div>
        ))}
      </section>

      <section className="py-20 bg-brand-sand grain relative">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 relative z-10">
          <Stats items={STATS} />
        </div>
      </section>

      <section className="py-24 md:py-32 max-w-screen-2xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-brand-terra">Who we serve</span>
            <h2 className="font-display text-4xl md:text-5xl text-brand-forest mt-3 tracking-tight">Our customers</h2>
          </Reveal>
          <div className="lg:col-span-8 flex flex-wrap gap-3">
            {CUSTOMERS.map((c, i) => (
              <Reveal key={c} delay={i * 0.04}>
                <span className="inline-block rounded-full border border-brand-forest/15 bg-white px-6 py-3 text-brand-forest font-medium">
                  {c}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 max-w-screen-2xl mx-auto px-6 md:px-12">
        <div className="rounded-[2.5rem] bg-brand-forest text-brand-bone p-10 md:p-16 flex flex-col md:flex-row md:items-center justify-between gap-8 grain relative overflow-hidden">
          <h2 className="font-display text-3xl md:text-5xl tracking-tight max-w-xl relative z-10">Let&apos;s build a lasting supply partnership.</h2>
          <Link to="/contact" className="relative z-10 inline-flex items-center gap-2 rounded-full bg-brand-leaf text-white px-8 py-4 font-medium hover:bg-brand-leaf-light transition-colors duration-300 active:scale-95 w-fit">
            Get in touch <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </>
  );
}
